import React, {useCallback, useEffect, useState} from 'react'
import {
  Box,
  CircularProgress,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import moment from 'moment'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import EmptyState from 'src/components-new/layout/EmptyState'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import Button from 'src/components-new/primitives/Button'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {CARD_BORDER_COLOR, CARD_BORDER_RADIUS, NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {PATHS, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'

const ASSUMPTION_STATE_OPTIONS = [
  {value: 'COMPLETED', label: 'Effettuata'},
  {value: 'PLANNED', label: 'Pianificata'},
  {value: 'TO_BE_PLANNED', label: 'In pianificazione'},
  {value: 'CANCELLED', label: 'Non effettuata'},
]

function getAssumptionStateColor(state: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (state === 'COMPLETED') return 'success'
  if (state === 'PLANNED') return 'info'
  if (state === 'TO_BE_PLANNED') return 'warning'
  if (state === 'CANCELLED') return 'error'
  return 'default'
}

function getAssumptionStateLabel(state: string | undefined): string {
  if (!state) return '—'
  return ASSUMPTION_STATE_OPTIONS.find(option => option.value === state)?.label ?? state
}

interface DiaryColumnConfig {
  id: string
  label?: string
  type?: string
  format?: string
  defaultValue?: string
  default?: string
  [key: string]: any
}

// Mirrors legacy TableCustom's renderCellValue default-case priority
// (src/components/elements/TableCustom/TableCustom.tsx): a column's `format`
// only applies for `type === 'date'`.
function formatColumnValue(col: DiaryColumnConfig, value: any): string {
  if (col.type === 'date' && col.format && value) {
    return moment(value).format(col.format)
  }
  if (value === null || value === undefined) {
    return col.defaultValue ?? col.default ?? '—'
  }
  if (typeof value === 'boolean') return value ? 'Sì' : 'No'
  return String(value)
}

// A date-type column's format is day-granularity (contains Y/M/D, e.g. 'ddd DD MMMM YYYY')
// as opposed to a pure time-of-day format (e.g. 'HH:mm', only lowercase tokens). Tells the
// "Data" column apart from the "Ora" column, so only the day-level one can be merged across
// same-day rows.
function isDateOnlyFormat(format: string | undefined): boolean {
  return Boolean(format && /[YMD]/.test(format))
}

// The merged/rowSpan view only makes sense when the PSP config actually gives us a day-level
// column ("Data") AND a separate time-level column ("Ora") to go with it — e.g. the 'quiperte'
// scope's DIARY_MORISKY columns (ANNO/MESE, both day-granularity, no time-of-day one) don't
// qualify, and correctly fall through to a normal, ungrouped table.
function isMergeEligible(columns: DiaryColumnConfig[], diaryType: string | undefined): boolean {
  if (diaryType === 'DIARY_MORISKY') return false
  const dateCols = columns.filter(col => col.type === 'date')
  const hasDayCol = dateCols.some(col => isDateOnlyFormat(col.format))
  const hasTimeCol = dateCols.some(col => !isDateOnlyFormat(col.format))
  return hasDayCol && hasTimeCol
}

interface DiaryRow {
  _id: string
  date?: string
  drug?: string
  state?: string
  [key: string]: any
}

const INITIAL_VIEWER_STATE = {open: false, filePath: '', mime: '', fileName: '', status: STATUS.PENDING}

function buildAssumptionDownloadName(row: DiaryRow): string {
  const dateStr = row.date
    ? new Date(row.date).toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '-')
    : ''
  return `Scheda_infusionale_${dateStr}.pdf`
}

function forceDownload(blob: Blob, downloadName: string): void {
  const url = (window.URL || window.webkitURL).createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = downloadName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

interface MergedDesktopDiaryTableProps {
  tableColumns: ColumnDef<DiaryRow>[]
  dayColumnIndex: number
  dayColumnId: string
  rows: DiaryRow[]
  loading: boolean
  emptyMessage: string
  pagination: PaginationState
  totalElements: number
  onPaginationChange: (next: PaginationState) => void
}

// Desktop-only: same visual chrome as DataTable (header row, card border, pagination footer)
// but with a real rowSpan on the day column so multiple medicines taken the same day share one
// "Data" cell instead of repeating it. Kept local to this page rather than added to the shared
// DataTable, since arbitrary cross-row rowSpan isn't a general table need — mobile (and any
// non-eligible PSP config) keeps using the plain DataTable below, unaffected by this component.
function MergedDesktopDiaryTable({
  tableColumns,
  dayColumnIndex,
  dayColumnId,
  rows,
  loading,
  emptyMessage,
  pagination,
  totalElements,
  onPaginationChange,
}: MergedDesktopDiaryTableProps): JSX.Element {
  const theme = useTheme()

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
        <CircularProgress />
      </Box>
    )
  }

  if (rows.length === 0) {
    return <EmptyState title="Nessun risultato" description={emptyMessage} />
  }

  const groups: DiaryRow[][] = []
  rows.forEach((row, idx) => {
    const previous = rows[idx - 1]
    const sameDayAsPrevious = Boolean(
      idx > 0 && row[dayColumnId] && previous?.[dayColumnId] && moment(row[dayColumnId]).isSame(previous[dayColumnId], 'day')
    )
    if (sameDayAsPrevious) groups[groups.length - 1].push(row)
    else groups.push([row])
  })

  const totalPages = Math.max(1, Math.ceil(totalElements / pagination.rowsPerPage))
  const headerCellSx = {
    color: NEUTRAL_SCALE[900],
    fontSize: 11,
    letterSpacing: 0.5,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    backgroundColor: NEUTRAL_SCALE[50],
    py: 1.5,
  }

  return (
    <Box sx={{borderRadius: CARD_BORDER_RADIUS, border: `1px solid ${CARD_BORDER_COLOR}`, overflow: 'hidden'}}>
      <Table>
        <TableHead>
          <TableRow sx={{backgroundColor: NEUTRAL_SCALE[50]}}>
            {tableColumns.map(col => (
              <TableCell key={col.key} width={col.width} sx={headerCellSx}>{col.header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map(group => group.map((row, rowIdx) => (
            <TableRow key={row._id} sx={{backgroundColor: theme.palette.background.paper}}>
              {tableColumns.map((col, colIdx) => {
                if (colIdx === dayColumnIndex) {
                  return rowIdx === 0
                    ? <TableCell key={col.key} rowSpan={group.length} sx={{py: 2, verticalAlign: 'top'}}>{col.render(row)}</TableCell>
                    : null
                }
                return <TableCell key={col.key} sx={{py: 2}}>{col.render(row)}</TableCell>
              })}
            </TableRow>
          )))}
        </TableBody>
      </Table>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderTop: `1px solid ${NEUTRAL_SCALE[200]}`,
          backgroundColor: NEUTRAL_SCALE[50],
          minHeight: 52,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Typography variant="body2" color="text.secondary">Righe per pagina:</Typography>
          <Select
            size="small"
            value={pagination.rowsPerPage}
            onChange={(e) => onPaginationChange({...pagination, rowsPerPage: Number(e.target.value), page: 0})}
            sx={{fontSize: 13, '& .MuiSelect-select': {py: 0.5, px: 1}}}
            variant="standard"
            disableUnderline
          >
            {DEFAULT_ROWS_PER_PAGE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </Select>
        </Box>

        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
          <Typography variant="body2" color="text.secondary">
            {`${pagination.page + 1} di ${totalPages} — Totale elementi: ${totalElements}`}
          </Typography>
          <Pagination
            count={totalPages}
            page={pagination.page + 1}
            onChange={(_e, value) => onPaginationChange({...pagination, page: value - 1})}
            color="primary"
            size="small"
          />
        </Box>
      </Box>
    </Box>
  )
}

/**
 * "Terapie" tab of the new-style "Diario paziente" page (src/pages-new/diary/DiaryNew.tsx) — the
 * primary-therapy assumption diary a PATIENT-role user sees for their own primary therapies.
 * Extracted from what used to be the whole of DiaryNew.tsx (before it grew a "Terapie Accessorie"
 * tab, see SecondaryAssumptionsPanel.tsx): scoped to therapyType 'PRIMARY' only, so the old
 * "Tipo" filter (Tutte/Terapia primaria/Terapia accessoria) that used to pick between merged
 * sources is gone — the tab itself now fixes the type.
 *
 * "Filtra" (Terapia/Data, same Filters convention as SecondaryAssumptionsPanel) is gated by the
 * PSP's `therapyDiaryConfigurations.showTherapiesFilter`, mirroring the legacy TherapyDiary.tsx
 * page this was extracted from.
 */
export default function PrimaryAssumptionsPanel(): JSX.Element {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const {columns = [], showExport, showTherapiesFilter, diaryType} = useAppSelector(
    state => state.pspConfigReducer.data.therapyDiaryConfigurations ?? {}
  )
  const {_id: patientId, serviceConf, firstName: userFirstName, lastName: userLastName} = useAppSelector(
    state => state.userReducer.data
  )

  const [rows, setRows] = useState<DiaryRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: ''})
  const [statusDownload, setStatusDownload] = useState<string>()
  const [viewerState, setViewerState] = useState(INITIAL_VIEWER_STATE)
  const [isExporting, setIsExporting] = useState(false)

  // "Terapia"/"Data" filter state — only ever populated once the patient actually applies the
  // "Filtra" drawer (see filtersConfig below); left unset, `drug`/`dateFrom`/`dateTo` are
  // omitted from the diary request, same "show everything" default as before this filter existed.
  // "Terapia" is by drug, not by therapy plan: a patient can have several plans for the same drug
  // (one per treatment cycle), and picking it shows the assumptions of all of them.
  const [therapyOptions, setTherapyOptions] = useState<Record<string, unknown>[]>([])
  const [selectedTherapy, setSelectedTherapy] = useState<string>()
  const [dateFrom, setDateFrom] = useState<string>()
  const [dateTo, setDateTo] = useState<string>()

  // DIARY_MORISKY is scored per single therapy plan (from its own startDate), so its filter keeps
  // listing plans and sending `therapy`; every other diary type filters by drug.
  const isMorisky = diaryType === 'DIARY_MORISKY'

  useEffect(() => {
    if (!showTherapiesFilter) return
    const fetchOptions = isMorisky ? apiClient.getTherapies : apiClient.getDiaryDrugs
    fetchOptions(() => {})
      .then((data: any) => setTherapyOptions(Array.isArray(data) ? data : []))
      .catch(() => setTherapyOptions([]))
  }, [showTherapiesFilter, isMorisky])

  const handleFiltersSubmit = (results: FilterResult[]): void => {
    const nextTherapy = results.find((r) => r.property === 'therapy')?.value as string | undefined
    const dateRange = results.find((r) => r.property === 'date')?.value as [string | null, string | null] | undefined

    setSelectedTherapy(nextTherapy)
    setDateFrom(dateRange?.[0] ?? undefined)
    setDateTo(dateRange?.[1] ?? undefined)
    setPagination((prev) => ({...prev, page: 0}))
  }

  // Morisky: same label priority as legacy TherapyDiary.tsx's therapyOptions helper.
  const therapySelectOptions = therapyOptions
    .map((option: any) => ({
      value: option._id,
      label: isMorisky
        ? option.planName ?? option.drug?.name ?? option.therapyType?.name ?? option._id
        : option.name,
    }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label), 'it', {sensitivity: 'base'}))

  const filtersConfig: FilterItem[] = [
    // showSearch: typing narrows the list (e.g. "Cer" -> Cerezyme) instead of scrolling it.
    {id: 'therapy', label: 'Terapia', type: 'select', showSearch: true, defaultValue: selectedTherapy ?? '', options: therapySelectOptions},
    // DIARY_MORISKY never gets a date filter — psp-backend's getDiaryMorisky doesn't support one —
    // and neither does a PSP that switched the "Data" column off.
    ...(diaryType !== 'DIARY_MORISKY' && columns.some((col: DiaryColumnConfig) => col.id === 'date') ? [{id: 'date', label: 'Data', type: 'rangePicker' as const}] : []),
  ]

  const fetchData = useCallback(() => {
    if (diaryType === 'DIARY_MORISKY') {
      Promise.all([
        apiClient.getDiaryMorisky({...pagination, therapy: selectedTherapy}, setStatus),
        apiClient.getDiaryMoriskyCount({therapy: selectedTherapy}, setStatusCount),
      ])
        .then(([data, countResult]) => {
          const list = Array.isArray(data) ? data : []
          setRows(list.map((item: any, i: number) => ({...item, _id: item._id ?? i})))
          const n = Number(countResult)
          if (!isNaN(n)) setCount(n)
        })
        .catch(() => setStatus(STATUS.REJECTED))
    } else {
      Promise.all([
        apiClient.getPatientDiary({...pagination, therapyType: 'PRIMARY', drug: selectedTherapy, dateFrom, dateTo}, setStatus),
        apiClient.getPatientDiaryCount({therapyType: 'PRIMARY', drug: selectedTherapy, dateFrom, dateTo}, setStatusCount),
      ])
        .then(([data, countResult]) => {
          const list = Array.isArray(data) ? data : []
          setRows(list.map((assumption: any, i: number) => ({...assumption, _id: assumption._id ?? i})))
          const n = Number(countResult)
          if (!isNaN(n)) setCount(n)
        })
        .catch(() => setStatus(STATUS.REJECTED))
    }
  }, [diaryType, pagination, selectedTherapy, dateFrom, dateTo])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) setViewerState(INITIAL_VIEWER_STATE)
  }, [statusDownload])

  const handlePreviewAssumption = async (row: DiaryRow) => {
    const response = await apiClient.exportPatientAssumption(patientId, row._id, setStatusDownload)
    if (!response) return
    const blob = new Blob([response.data], {type: 'application/pdf'})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    setViewerState({open: true, filePath: url, mime: 'application/pdf', fileName: buildAssumptionDownloadName(row), status: STATUS.FULFILLED})
  }

  const handleDownloadAssumption = async (row: DiaryRow) => {
    const response = await apiClient.exportPatientAssumption(patientId, row._id, setStatusDownload)
    if (!response) return
    const blob = new Blob([response.data], {type: 'application/pdf'})
    forceDownload(blob, buildAssumptionDownloadName(row))
  }

  const handleExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      if (diaryType === 'DIARY_DRUGS') {
        const response = await apiClient.downloadDiaryExcel(patientId, setStatusDownload)
        if (!response) return
        const url = window.URL.createObjectURL(response.data)
        const a = document.createElement('a')
        a.href = url
        a.download = 'Diario.xlsx'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } else {
        const fileResponse = await apiClient.generatePdf(`${new URL(window.location.href).origin}/${PATHS.PDF_DIARY}?filters=${JSON.stringify({})}`, setStatusDownload)
        if (!fileResponse) return
        const blob = new Blob([new Uint8Array(fileResponse)], {type: 'application/pdf'})
        const url = (window.URL || window.webkitURL).createObjectURL(blob)
        setViewerState({open: true, filePath: url, mime: 'application/pdf', fileName: `Diario - ${userFirstName} ${userLastName}`, status: STATUS.FULFILLED})
      }
    } finally {
      setIsExporting(false)
    }
  }

  // The 'tutor' scope's therapyDiaryConfigurations declares a placeholder {id: 'actions'}
  // column purely to label the header "Azioni" in the legacy TableCustom; the real action
  // icons are appended below as a dedicated ColumnDef, mirroring DrugsDiaryPanel.
  const filteredColumns = columns.filter((col: DiaryColumnConfig) => col.id !== 'actions' && (col.id !== 'adherence' || serviceConf === 'EXTRA'))

  // "Stato" always goes last among the data columns (right before Terapia/Azioni), regardless of
  // where the PSP config happens to list it — pulled out of the main config-column pass below and
  // re-appended after the Terapia badge instead of wherever it falls in `columns`.
  const stateColumn = filteredColumns.find((col: DiaryColumnConfig) => col.id === 'state')
  const nonStateColumns = filteredColumns.filter((col: DiaryColumnConfig) => col.id !== 'state')

  const tableColumns: ColumnDef<DiaryRow>[] = [
    ...nonStateColumns.map((col: DiaryColumnConfig, idx: number) => ({
      key: `${col.id}-${idx}`,
      header: col.label ?? '',
      render: (row: DiaryRow) => <Typography variant="body2">{formatColumnValue(col, row[col.id])}</Typography>,
    })),
    ...(stateColumn ? [{
      key: stateColumn.id,
      header: stateColumn.label ?? '',
      render: (row: DiaryRow) => <StatusPill label={getAssumptionStateLabel(row.state)} color={getAssumptionStateColor(row.state)} />,
    }] : []),
    // Preview/download only exist for the primary-therapy calendar (an "infusion sheet" PDF tied
    // to an `assumptions` document) — nothing equivalent was ever built for secondary-assumptions,
    // which is exactly why this tab is PRIMARY-only.
    ...(diaryType === 'DIARY_DRUGS' ? [{
      key: 'actions',
      header: '',
      width: 56,
      isActionColumn: true,
      render: (row: DiaryRow) => (
        <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{display: 'flex', gap: 0.5}}>
          <ActionIconButton variant="view" tooltip="Visualizza" onClick={() => handlePreviewAssumption(row)} />
          <ActionIconButton variant="download" tooltip="Scarica" onClick={() => handleDownloadAssumption(row)} />
        </Box>
      ),
    }] : []),
  ]

  // Merging only makes sense when the config actually gives us a separate day-level column
  // ("Data") and time-level column ("Ora") — otherwise (or on mobile, where DataTable's
  // stacked cards already show one date per card and don't need merging) this renders as a
  // perfectly normal table. Indexed against nonStateColumns (not filteredColumns), since that's
  // what tableColumns' leading section is actually built from now that "Stato" moved to the end.
  const dayColumnIndex = nonStateColumns.findIndex((col: DiaryColumnConfig) => col.type === 'date' && isDateOnlyFormat(col.format))
  const mergeEligible = isDesktop && dayColumnIndex !== -1 && isMergeEligible(nonStateColumns, diaryType)

  return (
    <Box>
      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel recuperare il diario paziente.'}
        </InlineMessage>
      )}

      {statusDownload === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusDownload(undefined)} sx={{mb: 2}}>
          {'Errore nel recuperare il diario paziente.'}
        </InlineMessage>
      )}

      {/* No page/table title here — HeaderBar already renders "Diario paziente" above;
          repeating it (or tableTitle from config) would just be a duplicate heading. */}
      {(showTherapiesFilter || showExport) && (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap'}}>
          {showTherapiesFilter && (
            <Filters key="primary-assumptions-filters" config={filtersConfig} onSubmit={handleFiltersSubmit} />
          )}
          {showExport && (
            <Button variant="primary" startIcon={<FileDownloadOutlinedIcon />} onClick={handleExport} loading={isExporting}>
              {'Esporta'}
            </Button>
          )}
        </Box>
      )}

      {mergeEligible ? (
        <MergedDesktopDiaryTable
          tableColumns={tableColumns}
          dayColumnIndex={dayColumnIndex}
          dayColumnId={nonStateColumns[dayColumnIndex].id}
          rows={rows}
          loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
          emptyMessage="Nessun dato disponibile"
          pagination={pagination}
          totalElements={count}
          onPaginationChange={setPagination}
        />
      ) : (
        <DataTable<DiaryRow>
          columns={tableColumns}
          rows={rows}
          keyField="_id"
          loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
          emptyMessage="Nessun dato disponibile"
          totalElements={count}
          pagination={pagination}
          onPaginationChange={setPagination}
          mobileActionsPosition="bottom"
        />
      )}

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={viewerState.mime}
        onClose={() => setViewerState(INITIAL_VIEWER_STATE)}
        open={viewerState.open}
        status={viewerState.status}
      />
    </Box>
  )
}
